import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { format } from 'date-fns';
import { useAuthStore } from '@/stores/authStore';
import { useBookingNotes, useAddBookingNote } from '@/hooks/useBookingNotes';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/stores/notificationStore';
import type { NoteCategory, WorkOrderNote } from '@/types';
import { describeApiError } from '@/lib/describeApiError';
import { dateFnsLocale } from '@/utils/locale';

interface LocalNote {
  category: NoteCategory;
  content: string;
  authorName: string;
  createdAt: string;
}

interface WorkOrderNotesPreviewProps {
  bookingId: string;
  creationMode?: boolean;
  localNotes?: LocalNote[];
  onLocalNoteAdd?: (category: NoteCategory, content: string) => void;
  onOpenDialog?: () => void;
  vertical?: boolean;
  customerNotes?: string | null;
  onCustomerNotesChange?: (value: string) => void;
}

const CHAT_CATEGORIES: NoteCategory[] = ['internal', 'manager'];

interface PreviewColumnProps {
  label: string;
  notes: WorkOrderNote[];
  isLast: boolean;
  vertical?: boolean;
  onSend: (content: string) => void;
  isSending?: boolean;
}

function PreviewColumn({ label, notes, isLast, vertical, onSend, isSending }: PreviewColumnProps) {
  const { t } = useTranslation('workorders');
  const user = useAuthStore((s) => s.user);
  const [message, setMessage] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const dateLocale = dateFnsLocale();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [notes.length]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isOwn = (note: WorkOrderNote) => note.authorId === user?.id;

  // API returns desc, reverse to oldest-first
  const sorted = [...notes].reverse();

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(vertical
          ? { borderBottom: isLast ? 0 : 1, pb: isLast ? 0 : 0.75, mb: isLast ? 0 : 0.75 }
          : { borderRight: isLast ? 0 : 1 }),
        borderColor: 'divider',
        px: 0.75,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, mb: 0.25, flexShrink: 0 }}
      >
        {label}
      </Typography>

      {/* Messages */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minHeight: 0,
          py: 0.25,
        }}
      >
        {sorted.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="caption" color="text.disabled">
              {t('notesHistory.noNotes')}
            </Typography>
          </Box>
        ) : (
          sorted.map((note) => {
            const own = isOwn(note);
            return (
              <Box
                key={note.id}
                sx={{
                  display: 'flex',
                  justifyContent: own ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    px: 1,
                    py: 0.5,
                    maxWidth: '90%',
                    bgcolor: own ? 'primary.main' : 'action.hover',
                    color: own ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 1.5,
                  }}
                >
                  {!own && note.authorName && (
                    <Typography
                      variant="caption"
                      color="inherit"
                      sx={{ fontWeight: 600, display: 'block', fontSize: '0.65rem', lineHeight: 1.2 }}
                    >
                      {note.authorName}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="inherit"
                    sx={{ fontSize: '0.75rem', lineHeight: 1.3, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                  >
                    {note.content}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      opacity: 0.7,
                      display: 'block',
                      textAlign: own ? 'right' : 'left',
                      color: 'inherit',
                    }}
                  >
                    {format(new Date(note.createdAt), 'd.M. HH:mm', { locale: dateLocale })}
                  </Typography>
                </Paper>
              </Box>
            );
          })
        )}
      </Box>

      {/* Input */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, mt: 0.25, flexShrink: 0 }}>
        <TextField
          fullWidth
          multiline
          maxRows={3}
          size="small"
          value={message}
          onChange={(e) => { setMessage(e.target.value); }}
          onKeyDown={handleKeyDown}
          placeholder={t('notesHistory.writePlaceholder')}
          sx={{
            minWidth: 0,
            '& .MuiInput-root': {
              fontSize: '0.75rem',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              overflow: 'auto !important',
            },
          }}
        />
        <Tooltip title={t('notesHistory.sendNote')}>
          <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSend}
              disabled={!message.trim() || isSending}
              aria-label={t('notesHistory.sendNote')}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

/** Simple editable text field for customer notes (replaces chat-style column) */
function CustomerNotesField({
  value,
  onChange,
  onFocus,
  onBlurSave,
  isLast,
  vertical,
  creationMode,
}: {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlurSave: () => void;
  isLast: boolean;
  vertical?: boolean;
  creationMode?: boolean;
}) {
  const { t } = useTranslation('workorders');

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(vertical
          ? { borderBottom: isLast ? 0 : 1, pb: isLast ? 0 : 0.75, mb: isLast ? 0 : 0.75 }
          : { borderRight: isLast ? 0 : 1 }),
        borderColor: 'divider',
        px: 0.75,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, mb: 0.25, flexShrink: 0 }}
      >
        {t('notesHistory.customerTab')}
      </Typography>
      <TextField
        fullWidth
        multiline
        maxRows={10}
        size="small"
        value={value}
        onChange={(e) => { onChange(e.target.value); }}
        onFocus={onFocus}
        onBlur={creationMode ? undefined : onBlurSave}
        placeholder={t('create.customerNotesPlaceholder')}
        sx={{
          flex: 1,
          display: 'flex',
          '& .MuiInput-root': {
            fontSize: '0.75rem',
            flex: 1,
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            overflow: 'auto !important',
          },
        }}
      />
    </Box>
  );
}

export function WorkOrderNotesPreview({
  bookingId,
  creationMode,
  localNotes,
  onLocalNoteAdd,
  onOpenDialog,
  vertical,
  customerNotes: customerNotesProp,
  onCustomerNotesChange,
}: WorkOrderNotesPreviewProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const showManagerNotes = useAuthStore((s) => s.hasRole(['manager', 'owner', 'admin']));

  const { data: notes = [] } = useBookingNotes(creationMode ? undefined : bookingId);
  const addNote = useAddBookingNote();
  const updateBooking = useUpdateBooking();

  // Local state for customer notes text field
  const [customerNotesLocal, setCustomerNotesLocal] = useState(customerNotesProp ?? '');
  const initialValueRef = useRef(customerNotesProp ?? '');
  const isFocusedRef = useRef(false);

  // Sync when prop changes (e.g. after refetch)
  useEffect(() => {
    // Don't sync if the user is actively editing (field is focused)
    if (isFocusedRef.current) return;
    setCustomerNotesLocal(customerNotesProp ?? '');
    initialValueRef.current = customerNotesProp ?? '';
  }, [customerNotesProp]);

  const handleCustomerNotesChange = (value: string) => {
    setCustomerNotesLocal(value);
    onCustomerNotesChange?.(value);
  };

  const handleCustomerNotesFocus = () => {
    isFocusedRef.current = true;
  };

  const handleCustomerNotesBlurSave = () => {
    isFocusedRef.current = false;
    if (creationMode) return;
    if (customerNotesLocal === initialValueRef.current) return;
    updateBooking.mutate(
      { id: bookingId, data: { customerNotes: customerNotesLocal } },
      {
        onSuccess: () => {
          initialValueRef.current = customerNotesLocal;
          notify.success(t('notesHistory.customerNotesSaved'));
        },
        onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('noteErrors.customerNotesSaveFailed') })),
      },
    );
  };

  const visibleChatCategories = showManagerNotes
    ? CHAT_CATEGORIES
    : CHAT_CATEGORIES.filter((c) => c !== 'manager');

  const categoryLabels: Record<NoteCategory, string> = {
    customer: t('notesHistory.customerTab'),
    internal: t('notesHistory.internalTab'),
    manager: t('notesHistory.managerTab'),
  };

  const countByCategory = (cat: NoteCategory) => {
    if (creationMode) {
      return localNotes?.filter((n) => n.category === cat).length ?? 0;
    }
    return notes.filter((n) => n.category === cat).length;
  };

  const handleSend = (category: NoteCategory, content: string) => {
    if (creationMode) {
      onLocalNoteAdd?.(category, content);
      return;
    }
    addNote.mutate(
      { bookingId, category, content },
      {
        onSuccess: () => notify.success(t('notesHistory.noteSent')),
        onError: () => notify.error(tc('error'), t('notesHistory.noteSendFailed')),
      },
    );
  };

  const buildNotes = (category: NoteCategory): WorkOrderNote[] => {
    if (creationMode) {
      const user = useAuthStore.getState().user;
      return (localNotes ?? [])
        .filter((n) => n.category === category)
        .map((ln, i) => ({
          id: `local-${category}-${String(i)}`,
          bookingId: '',
          category: ln.category,
          content: ln.content,
          authorId: user?.id ?? '',
          authorName: ln.authorName,
          createdAt: ln.createdAt,
          updatedAt: null,
        }));
    }
    return notes.filter((n) => n.category === category);
  };

  // All visible columns: customer (text field) + chat categories
  const allColumns = ['customer' as const, ...visibleChatCategories];
  const totalColumns = allColumns.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header with expand button */}
      {onOpenDialog && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0, mb: 0.5 }}>
          <Tooltip title={t('notesHistory.notesTitle')}>
            <IconButton
              size="small"
              onClick={onOpenDialog}
              aria-label={t('notesHistory.notesTitle')}
              sx={{ p: 0.25 }}
            >
              <OpenInFullIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Columns */}
      <Box sx={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', flex: 1, minHeight: 0 }}>
        {/* Customer notes — simple text field */}
        <CustomerNotesField
          value={customerNotesLocal}
          onChange={handleCustomerNotesChange}
          onFocus={handleCustomerNotesFocus}
          onBlurSave={handleCustomerNotesBlurSave}
          isLast={totalColumns === 1}
          vertical={vertical}
          creationMode={creationMode}
        />

        {/* Chat columns for internal / manager */}
        {visibleChatCategories.map((cat, idx) => {
          const count = countByCategory(cat);
          const label = count > 0
            ? `${categoryLabels[cat]} (${String(count)})`
            : categoryLabels[cat];

          return (
            <PreviewColumn
              key={cat}
              label={label}
              notes={buildNotes(cat)}
              isLast={idx === visibleChatCategories.length - 1}
              vertical={vertical}
              onSend={(content) => { handleSend(cat, content); }}
              isSending={addNote.isPending}
            />
          );
        })}
      </Box>
    </Box>
  );
}
